import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

import AnimateHeight from 'react-animate-height';
import { useDispatch, useSelector } from 'react-redux';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { colors } from '@atlaskit/theme';
import { DiffContent, OnAddCommentArgs } from '@atlassian/bitkit-diff';

import {
  apiCommentId,
  toConversationId,
} from 'src/components/conversation-provider/utils/to-conversation';
import { useOptionalConversationsContext } from 'src/contexts/conversations-context';
import { toggleDiffComments } from 'src/redux/pull-request/actions';
import {
  getHighlightCommentThread,
  getHighlighted,
} from 'src/redux/pull-request/selectors';
import { CommentThreadWrapper } from 'src/sections/repository/styles/page';
import {
  getInlineThreadsForFile,
  getOutdatedCommentsCountForFile,
} from 'src/selectors/conversation-selectors';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { Diff as DiffType } from 'src/types/pull-request';
import { BucketState } from 'src/types/state';
import { extractFilepath } from 'src/utils/extract-file-path';

import diffMessages from '../../components/diff.i18n';
import DiscardCommentsDialog, {
  useDiscardCommentsDialogState,
} from '../../components/discard-comments-dialog';
import { filterCodeReviewThreads } from '../../components/tabs/diff/utils';
import {
  NewPullRequestConversation,
  PullRequestConversation,
} from '../../containers/conversation/pull-request-conversation';
import { useHiddenComments } from '../diffs';

type ConversationMeta = {
  file: DiffType;
  lineFrom: string | number | undefined;
  lineTo: string | number | undefined;
};

function isNewConversationForThisLine(
  newConvoMeta: ConversationMeta | null,
  fileName: string,
  diffLine: DiffContent
) {
  // check if this is an invalid conversation or a file-level conversation
  // (which shouldn't be rendered inline and won't have a 'from' and 'to' field)
  if (
    !newConvoMeta ||
    !newConvoMeta.file ||
    (!newConvoMeta.lineFrom && !newConvoMeta.lineTo)
  ) {
    return false;
  }

  return (
    (newConvoMeta.file.to === fileName ||
      newConvoMeta.file.from === fileName) &&
    newConvoMeta.lineTo === diffLine.lineTo &&
    newConvoMeta.lineFrom === diffLine.lineFrom
  );
}

type UseConversationsOptions = {
  diff: DiffType;
  editorPopupsMountPointRef: React.MutableRefObject<HTMLElement | null>;
  onInlineEditorClose?: () => void;
  onInlineEditorOpen?: () => void;
  portalParentIdPrefix?: string;
};

export const usePullRequestInlineConversations = (
  options: UseConversationsOptions
) => {
  const {
    diff,
    editorPopupsMountPointRef,
    onInlineEditorClose,
    onInlineEditorOpen,
    portalParentIdPrefix = 'portal-parent-pr-inline-conversation-',
  } = options;

  const [newConversationMeta, setNewConversationMeta] =
    useState<ConversationMeta | null>(null);

  const conversationSelectorInput = useMemo(() => diff, [diff]);
  const dispatch = useDispatch();
  const threads = useSelector((state: BucketState) =>
    getInlineThreadsForFile(state, conversationSelectorInput)
  );
  const areCommentsHidden = useHiddenComments(diff);
  const [shouldShowInlineContent, setShouldShowInlineContent] = useState(
    !areCommentsHidden
  );
  useEffect(() => {
    if (!areCommentsHidden) {
      setShouldShowInlineContent(true);
    }
  }, [areCommentsHidden]);
  const isAnonymousUser = useSelector(getCurrentUserIsAnonymous);
  const outdatedConversationsCount = useSelector((state: BucketState) =>
    getOutdatedCommentsCountForFile(state, conversationSelectorInput)
  );

  const isCommentThreadHighlighted = useSelector(getHighlightCommentThread);
  const highlightedCommentId = useSelector(getHighlighted);

  const filepath = extractFilepath(diff);
  const isEditorOpen = !!newConversationMeta;

  useEffect(() => {
    setNewConversationMeta(null);
  }, [threads.length, outdatedConversationsCount, setNewConversationMeta]);

  const { confirmDiscardComments, dialogProps, isDialogOpen } =
    useDiscardCommentsDialogState();

  const handleAddInlineComment = useCallback(
    (line: OnAddCommentArgs) => {
      if (areCommentsHidden) {
        dispatch(toggleDiffComments(filepath));
      }
      const addInlineComment = () =>
        setNewConversationMeta({
          file: diff,
          lineTo: line.to,
          lineFrom: line.from,
        });

      // We only want 1 editor for creating a new inline conversation thread to be open at any given time
      if (isEditorOpen) {
        confirmDiscardComments({
          message: diffMessages.discardCommentsModalBody__SameFileComment,
          onConfirmDiscard: () => {
            addInlineComment();
          },
        });
      } else {
        addInlineComment();
      }
    },
    [
      dispatch,
      filepath,
      confirmDiscardComments,
      diff,
      isEditorOpen,
      areCommentsHidden,
      setNewConversationMeta,
    ]
  );

  const handleEditorCancel = useCallback(() => {
    setNewConversationMeta(null);
  }, [setNewConversationMeta]);

  const conversationsContext = useOptionalConversationsContext();
  const diffSrc = conversationsContext?.conversationProvider?.config?.anchor;
  const diffDest = conversationsContext?.conversationProvider?.config?.destRev;

  const shouldHighlightCurrentConversation = useCallback(
    (diffLine: DiffContent) => {
      if (!(isCommentThreadHighlighted && highlightedCommentId)) {
        return false;
      }

      const { lineTo, lineFrom } = diffLine;
      const inlineThreads = threads.filter(thread => {
        const meta = thread.topComment.inline;
        if (!meta) {
          return false;
        }
        const { to, from } = meta;
        return (!!lineTo && to === lineTo) || (!!lineFrom && from === lineFrom);
      });

      const conversationIds = inlineThreads.map(thread =>
        apiCommentId(thread.topComment)
      );

      return conversationIds.includes(highlightedCommentId);
    },
    [threads, isCommentThreadHighlighted, highlightedCommentId]
  );

  /* HOW THIS WORKS:
   * This function gets called EVERY time a code line is rendered or re-rendered,
   * and also for rendering file-level conversations.  Right now it's called *twice*
   * per code line - needs to be fixed.
   *
   * Note that a `newConversation` refers specifically to a new root level Conversation,
   * not a reply to an existing comment.
   */
  const renderInlineConversations = useCallback(
    (diffLine: DiffContent) => {
      if (!shouldShowInlineContent) {
        return null;
      }

      const newConversationIsForThisLine = isNewConversationForThisLine(
        newConversationMeta,
        filepath,
        diffLine
      );

      const { lineTo, lineFrom } = diffLine;
      const inlineThreads = threads.filter(thread => {
        const meta = thread.topComment.inline;
        if (!meta) {
          return false;
        }
        const { to, from } = meta;
        return (!!lineTo && to === lineTo) || (!!lineFrom && from === lineFrom);
      });

      // Nothing to render
      if (!inlineThreads.length && !newConversationIsForThisLine) {
        return null;
      }

      const onNewConversationEditorOpen = () => {
        if (typeof onInlineEditorOpen === 'function') {
          onInlineEditorOpen();
        }
        if (
          typeof conversationsContext?.eventHandlers?.onNewConversationClick ===
          'function'
        ) {
          conversationsContext?.eventHandlers?.onNewConversationClick({
            file: filepath,
            lineTo,
            lineFrom,
          });
        }
      };

      const newConversation = newConversationMeta && (
        <NewPullRequestConversation
          editorPopupsMountPoint={editorPopupsMountPointRef.current}
          meta={{
            path: filepath,
            to: diffLine.lineTo,
            from: diffLine.lineFrom,
          }}
          portalParentId={`${portalParentIdPrefix}-new`}
          onEditorOpen={onNewConversationEditorOpen}
          onEditorClose={onInlineEditorClose}
          onCancel={handleEditorCancel}
          commentThreads={[]}
          diffLine={diffLine}
        />
      );

      const filteredCSLRConversations = () => {
        const filteredThreads = filterCodeReviewThreads(
          inlineThreads,
          diffSrc,
          diffDest,
          conversationsContext?.baseRevHash
        );
        return filteredThreads.length === 0 &&
          !newConversationIsForThisLine ? null : (
          <Fragment>
            <CommentThreadWrapper
              style={
                shouldHighlightCurrentConversation(diffLine)
                  ? { borderColor: `${colors.B100}` }
                  : {}
              }
            >
              {filteredThreads.map(thread => {
                const conversationId = toConversationId(thread.topComment);
                return (
                  <AnimateHeight
                    key={`transition-${conversationId}`}
                    duration={250}
                    animateOpacity
                    height={areCommentsHidden ? 0 : 'auto'}
                    onAnimationEnd={() => setShouldShowInlineContent(false)}
                  >
                    <PullRequestConversation
                      editorPopupsMountPoint={editorPopupsMountPointRef.current}
                      key={filepath}
                      meta={{
                        path: filepath,
                        to: diffLine.lineTo || diffLine.lineFrom,
                      }}
                      portalParentId={`${portalParentIdPrefix}-${conversationId}`}
                      onEditorOpen={onInlineEditorOpen}
                      onEditorClose={onInlineEditorClose}
                      canCollapseThread
                      canResolveThread={
                        conversationsContext?.supportsCommentResolution
                      }
                      commentThreads={[thread]}
                      diffLine={diffLine}
                    />
                  </AnimateHeight>
                );
              })}
              {!areCommentsHidden &&
                newConversationIsForThisLine &&
                newConversation}
            </CommentThreadWrapper>
          </Fragment>
        );
      };
      return filteredCSLRConversations();
    },
    [
      editorPopupsMountPointRef,
      filepath,
      areCommentsHidden,
      shouldShowInlineContent,
      handleEditorCancel,
      newConversationMeta,
      onInlineEditorClose,
      onInlineEditorOpen,
      conversationsContext?.eventHandlers,
      portalParentIdPrefix,
      shouldHighlightCurrentConversation,
      diffSrc,
      diffDest,
      conversationsContext?.baseRevHash,
      threads,
      conversationsContext?.supportsCommentResolution,
    ]
  );

  const discardCommentsModal = useMemo(
    () => (
      <ModalTransition>
        {isDialogOpen && <DiscardCommentsDialog {...dialogProps} />}
      </ModalTransition>
    ),
    [dialogProps, isDialogOpen]
  );

  return {
    discardCommentsModal,
    onAddInlineComment: isAnonymousUser ? undefined : handleAddInlineComment,
    renderInlineConversations,
  };
};
